import {
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { format } from 'date-fns';
import type { FunctionComponent } from 'react';
import { paletteHelper } from '@property.com.au/ui-kit';
import styled, { useTheme } from 'styled-components';
import type { SupplyDemandAndPerformanceGraph } from './types';
import { SupplyDemandAndPerformanceGraphXTick } from './subcomponents/SupplyDemandAndPerformanceGraphXTick';
import { SupplyDemandAndPerformanceBrickGraphTooltip } from './subcomponents/SupplyDemandAndPerformanceGraphTooltip';
import { mapGraphVariantToColors } from '../../../../../components/TimeseriesAreaGraph/TimeseriesAreaGraph';

const LegendText = styled.span`
  color: ${paletteHelper('textSecondary')};
`;

export const formatXAxisValue = (value: string): string => `${format(new Date(value), 'yyyy')}`;

export const formatLegendValue = (value: string) => {
  const formattedValue = value === 'supply' ? 'Similar for sale' : 'Potential buyers';

  return <LegendText>{formattedValue}</LegendText>;
};

const responsiveContainerHeight = 205;
const activeDotStrokeWidth = 2.5;
const activeDotRadius = 3.5;
const dotLineStrokeWidth = 2.3;
const xAxisPadding = 20;
const legendPaddingTop = '22px';
const legendFontSize = '12px';
const yAxisOffset = -0.2;

interface MedianCostBrickGraphProps {
  graph: SupplyDemandAndPerformanceGraph;
  className?: string;
  title: string;
}

export const EmptySupplyDemandAndPerformanceBrickGraph = styled.div`
  height: ${responsiveContainerHeight}px;
`;

export const SupplyDemandAndPerformanceBrickGraph: FunctionComponent<MedianCostBrickGraphProps> = ({
  graph: { dataPoints, supplyVariant, demandVariant },
  className,
  title,
}) => {
  const theme = useTheme();
  const supplyVariantStyling = mapGraphVariantToColors(supplyVariant, theme);
  const demandVariantStyling = mapGraphVariantToColors(demandVariant, theme);

  return (
    <ResponsiveContainer
      className={className}
      aria-label={title}
      width="100%"
      height={responsiveContainerHeight}
    >
      <ComposedChart data={dataPoints}>
        <RTooltip
          filterNull={false}
          content={<SupplyDemandAndPerformanceBrickGraphTooltip />}
          wrapperStyle={{ outline: 'none' }}
        />
        <XAxis
          dataKey="date"
          domain={['auto', 'auto']}
          padding={{ left: xAxisPadding, right: xAxisPadding }}
          axisLine={{ stroke: theme.palette.borderPrimary }}
          tickLine={{ stroke: theme.palette.borderPrimary }}
          tick={<SupplyDemandAndPerformanceGraphXTick />}
          tickFormatter={formatXAxisValue}
        />
        <YAxis type="number" domain={[yAxisOffset, 'dataMax']} hide />j
        <Line
          dataKey="supply"
          type="linear"
          stroke={supplyVariantStyling.darkColor}
          strokeWidth={dotLineStrokeWidth}
          dot={{
            stroke: supplyVariantStyling.lightColor,
            strokeWidth: activeDotStrokeWidth,
            fill: '#FFFFFF',
            r: activeDotRadius,
          }}
        />
        <Line
          dataKey="demand"
          type="linear"
          stroke={demandVariantStyling.darkColor}
          strokeWidth={dotLineStrokeWidth}
          strokeDasharray="5 5"
          dot={{
            stroke: demandVariantStyling.lightColor,
            strokeWidth: activeDotStrokeWidth,
            strokeDasharray: 0,
            fill: '#FFFFFF',
            r: activeDotRadius,
          }}
        />
        <Legend
          align="left"
          iconType="plainline"
          formatter={formatLegendValue}
          wrapperStyle={{ fontSize: legendFontSize, paddingTop: legendPaddingTop }}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};
